import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import {
  FieldObjectType,
  IAttendantTemplateData,
  IAttendantTemplateInputItem,
  InputType,
  SupportedLanguage,
  ValidationType,
} from "interfaces";
import { get } from "lodash";
import { Fragment, useState } from "react";
import {
  DATE_INPUT_VALIDATIONS,
  FIELD_OBJECT_LIST,
  NUMBER_INPUT_VALIDATIONS,
  TEXT_INPUT_VALIDATIONS,
} from "utills/constants";
import { RightSideModal } from "components/rightSideModal";
import { IModalProps } from "components/modal";
import { InputTypeField } from "../inputType";
import { Options } from "../options";
import { CollapsibleSection } from "components/collapsibleSection";
import { useTranslation } from "@refinedev/core";

interface IFieldFormProps extends IModalProps {
  groupData: IAttendantTemplateData;
  languages: SupportedLanguage[];
  isOpenRegistration: boolean;
}

export const Fields = ({
  groupData,
  languages,
  open,
  onClose,
}: IFieldFormProps) => {
  const { translate } = useTranslation();
  const [openFieldId, setOpenFieldId] = useState<number | null>(null);

  const _handleOpenField = (index: number) => {
    setOpenFieldId((prevId) => (prevId === index ? null : index));
  };

  const getValidationList = (type: InputType) => {
    switch (type) {
      case InputType.TEXT:
        return TEXT_INPUT_VALIDATIONS;
      case InputType.DATE:
        return DATE_INPUT_VALIDATIONS;
      case InputType.NUMBER:
        return NUMBER_INPUT_VALIDATIONS;
      default:
        return [];
    }
  };

  const isShowOptionForm = (field: IAttendantTemplateInputItem) => {
    return (
      (field.type === InputType.SELECT &&
        (field?.object === FieldObjectType.CUSTOM ||
          field?.object === FieldObjectType.TSHIRT_SIZE_FINISHER ||
          field?.object === FieldObjectType.TSHIRT_SIZE)) ||
      field.type === InputType.RADIO_BUTTON ||
      field.type === InputType.MULTI_SELECT
    );
  };
  console.log(groupData);
  return (
    <RightSideModal
      title={translate("eventSetup.participantTemplate.addField")}
      closeOnClickBackdrop={false}
      open={open}
      onClose={onClose}
    >
      <div className="px-4 py-4 sm:px-6">
        <div>
          <p className="text-sm font-medium">{translate("eventSetup.participantTemplate.groupInfo")}</p>
          <div className="mt-1 flex flex-row flex-wrap items-center gap-x-2">
            {languages.map((lang, langIndex) => (
              <Fragment key={lang}>
                {langIndex !== 0 ? <span>•</span> : <></>}
                <p className="text-sm" style={{ wordBreak: "break-word" }}>
                  {get(groupData, `group.label.${lang}`, "")}
                </p>
              </Fragment>
            ))}
          </div>
        </div>
        <div className="mt-6 pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm font-medium">{translate("eventSetup.participantTemplate.fieldsSetup")}</p>
        </div>
        <div className="mt-4 sm:mt-6">
          <div className="grid sm:grid-cols-6 lg:gap-x-6">
            <div className="col-span-full">
              {(groupData.fields ?? []).map(
                (field: IAttendantTemplateInputItem, index: number) => (
                  <div
                    className={`border rounded-lg mb-4 ${
                      openFieldId === index
                        ? "border-brand-500"
                        : "border-gray-300"
                    }`}
                    key={field.key}
                  >
                    <CollapsibleSection
                      label={`${get(
                        field,
                        `label.${
                          languages.includes(SupportedLanguage.EN)
                            ? "en"
                            : languages[0] ?? "vi"
                        }`
                      )}`}
                      customOpen={{
                        open: openFieldId === index,
                        onClick: () => _handleOpenField(index),
                      }}
                      isAnimated={false}
                    >
                      <div className="flex flex-col gap-y-4 px-4 py-4 sm:px-6 text-sm">
                        <div>
                          <Label required id={`type_${index}`} name="Type" />
                          <InputTypeField value={field.type} />
                        </div>
                        <div>
                          <Label
                            required
                            id={`label_${index}`}
                            name={translate("eventSetup.participantTemplate.field.fieldName")}
                          />
                          <div className="flex flex-col gap-y-4">
                            {languages.map((lang: SupportedLanguage) => (
                              <div key={lang}>
                                <div className="relative flex items-center gap-x-2">
                                  <img
                                    src={`/images/flags/circle-${lang}.png`}
                                    alt="Language Icon"
                                    className="size-5"
                                  />
                                  {get(field.label, lang, "")}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label id={`sub_label_${index}`} name={translate("eventSetup.participantTemplate.field.description")} />
                          <div className="flex flex-col gap-y-4">
                            {languages.map((lang: SupportedLanguage) => (
                              <div key={lang}>
                                <div className="relative flex items-center gap-x-2 text-sm">
                                  <img
                                    src={`/images/flags/circle-${lang}.png`}
                                    alt="Language Icon"
                                    className="size-5"
                                  />
                                  {get(field.sub_label, lang, "") || (
                                    <span className="text-gray-400">N/A</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label
                            id={`sub_label_url`}
                            name={translate("eventSetup.participantTemplate.field.hyperlinkForDescription")}
                          />
                          <div className="flex flex-col gap-y-4">
                            {field.sub_label_url || (
                              <span className="text-gray-400">N/A</span>
                            )}
                          </div>
                        </div>
                        <div>
                          <Label
                            required
                            id={`key`}
                            name="Key"
                            hint={translate("eventSetup.participantTemplate.field.keyHint")}
                          />
                          {field.key}
                        </div>
                        <div className="grid grid-cols-1 items-center sm:grid-cols-3 gap-x-1 gap-y-4">
                          <div>
                            <SwitchButton
                              label={translate("eventSetup.participantTemplate.field.show")}
                              checked={field.show}
                              onChange={() => {}}
                              disabled={true}
                            />
                          </div>
                          <div>
                            <SwitchButton
                              label={translate("eventSetup.participantTemplate.field.required")}
                              checked={field.require}
                              onChange={() => {}}
                              disabled={true}
                            />
                          </div>
                          <div>
                            <SwitchButton
                              label={translate("eventSetup.participantTemplate.field.lockEdit")}
                              checked={field.is_lock_update_data}
                              onChange={() => {}}
                              disabled={true}
                            />
                          </div>
                        </div>
                        {getValidationList(field.type).length > 0 ? (
                          <div>
                            <Label id={`validation`} name={translate("eventSetup.participantTemplate.field.validation")} />
                            {
                              getValidationList(field.type ?? "").find(
                                (data) => data.value === field.validation
                              )?.label || (
                                <span className="text-gray-400">N/A</span>
                              )
                            }
                          </div>
                        ) : (
                          <></>
                        )}
                        {field.validation === ValidationType.LENGTH ||
                        field.validation === ValidationType.DOB ? (
                          <div className="grid grid-cols-1 items-start sm:grid-cols-2 gap-x-4 gap-y-4 bg-magnolia-600 px-4 py-4 sm:px-6 -mx-4 sm:-mx-6 -mb-4 rounded-bl-lg rounded-br-lg">
                            <div>
                              <Label required id="min" name={translate("eventSetup.participantTemplate.field.min")} />
                              {get(field, "option.min", "")}
                            </div>
                            <div>
                              <Label required id="max" name={translate("eventSetup.participantTemplate.field.max")} />
                              {get(field, "option.max", "")}
                            </div>
                            {field?.validation === ValidationType.DOB ? (
                              <div className="w-full col-span-full">
                                <Label required id="by" name={translate("eventSetup.participantTemplate.field.calculateAge")} />
                                {get(field, "option.max", "")}
                              </div>
                            ) : (
                              <></>
                            )}
                          </div>
                        ) : (
                          <></>
                        )}
                        {field.type === InputType.SELECT ? (
                          <div>
                            <Label required id={`object`} name={translate("eventSetup.participantTemplate.field.object")} />
                            {
                              FIELD_OBJECT_LIST.find(
                                (selected: { label: string; value: any }) =>
                                  selected.value === field.object
                              )?.label
                            }
                          </div>
                        ) : (
                          <></>
                        )}
                        {isShowOptionForm(field) ? (
                          <Options fieldData={field} languages={languages} />
                        ) : (
                          <></>
                        )}
                      </div>
                    </CollapsibleSection>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </RightSideModal>
  );
};
